import { useCallback, useEffect } from 'react'

import { useNavOverlay, NAV_OVERLAY_IDS } from './useNavOverlay'

const SCROLL_LOCK_CLASS = 'disable-page-scroll'

// Open/close state for the redesigned mobile navigation. Backed by the shared
// nav-overlay coordinator so opening the hamburger closes any open popover / user
// menu and vice versa - only one top-nav overlay is ever open at a time.
function useHamburgerStatus() {
  const { isOpen: isActive, open, close } = useNavOverlay(NAV_OVERLAY_IDS.HAMBURGER)

  const toggle = useCallback(() => {
    if (isActive) {
      close(NAV_OVERLAY_IDS.HAMBURGER)
    } else {
      open(NAV_OVERLAY_IDS.HAMBURGER)
    }
  }, [isActive, open, close])

  // Lock body scroll while the hamburger menu is open. The action popovers lock the
  // same class (useActionScrollLock); the coordinator can swap hamburger -> cart in a
  // single commit, flipping `isActive` false here as the cart opens. Removing the lock
  // ONLY in cleanup (never in the effect body) is deliberate: React runs every effect
  // cleanup before any setup, so this removal lands before the cart's setup adds the
  // class back - otherwise the child cart effect would add it first and this parent
  // effect would then strip it, leaving the open cart scrollable.
  useEffect(() => {
    if (!isActive) {
      return undefined
    }

    document.body.classList.add(SCROLL_LOCK_CLASS)

    return () => {
      document.body.classList.remove(SCROLL_LOCK_CLASS)
    }
  }, [isActive])

  // The hamburger only exists below md (960px); the menu is inline above it. If the
  // menu was opened on mobile and the viewport widens to desktop, `isActive` would
  // otherwise stay true and the menu would reappear open on the next return to mobile.
  // Reset it whenever the viewport reaches the desktop layout so the hamburger starts
  // closed each time it becomes relevant again.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined
    }

    const query = window.matchMedia('(min-width: 960px)')

    function handleChange(event) {
      if (event.matches) {
        close(NAV_OVERLAY_IDS.HAMBURGER)
      }
    }

    query.addEventListener('change', handleChange)

    return () => query.removeEventListener('change', handleChange)
  }, [close])

  // Mobile in-place listing category switches (useListingCategoryNavigation)
  // preventDefault the link, so no page load ever closes the menu - close it
  // explicitly or it stays open over the refreshed results.
  useEffect(() => {
    function closeHamburger() {
      close(NAV_OVERLAY_IDS.HAMBURGER)
    }

    window.addEventListener('listing-page:navigate-category-mobile', closeHamburger)

    return () => window.removeEventListener('listing-page:navigate-category-mobile', closeHamburger)
  }, [close])

  return { isActive, toggle }
}

export { useHamburgerStatus }
